import { useEffect, useMemo, useRef, useState } from 'react'
import { useParams, Link } from 'react-router-dom'
import { useAppSelector } from '../../hooks/redux'
import {
  getVendors,
  createVendor,
  updateVendor,
  deleteVendor,
} from '../../services/api/finance'
import type {
  Vendor,
  VendorBankAccountInput,
  VendorCreateInput,
  VendorType,
} from '../../types/finance'
import { useToast } from '../../components/ToastContainer'
import { useFinancePageSize, FINANCE_PAGE_SIZE_OPTIONS } from '../../hooks/useFinancePageSize'
import usePermissions, { Permission } from '../../hooks/usePermissions'
import { FinanceAuditLog } from '../../components/finance'
import {
  BuildingStorefrontIcon,
  PlusIcon,
  PencilIcon,
  TrashIcon,
  MagnifyingGlassIcon,
  XMarkIcon,
  ArrowLeftIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
} from '@heroicons/react/24/outline'

interface FormBank extends VendorBankAccountInput {
  _localId: string
}

interface FormState {
  type: VendorType
  name: string
  taxId: string
  contactName: string
  contactPhone: string
  address: string
  tags: string[]
  note: string
  bankAccounts: FormBank[]
}

const emptyForm: FormState = {
  type: 'EXPENSE',
  name: '',
  taxId: '',
  contactName: '',
  contactPhone: '',
  address: '',
  tags: [],
  note: '',
  bankAccounts: [],
}

const newBank = (): FormBank => ({
  _localId: `bank-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
  accountName: '',
  bankName: '',
  accountNumber: '',
  isPrimary: false,
})

const TYPE_LABEL: Record<VendorType, { label: string; cls: string }> = {
  INCOME: { label: '收入', cls: 'bg-emerald-100 text-emerald-800' },
  EXPENSE: { label: '支出', cls: 'bg-rose-100 text-rose-800' },
}

// 台灣統一編號檢查碼
// 規則：8 碼 × 倍數 [1,2,1,2,1,2,4,1]，各乘積取個位數加十位數加總
// 第 7 碼若為 7：sum 或 sum+1 為 10 的倍數皆視為有效
// 其餘：sum 為 10 的倍數
const validateTaiwanTaxId = (raw: string): { ok: boolean; reason?: string } => {
  const s = String(raw || '').trim()
  if (s === '') return { ok: true }
  if (!/^\d{8}$/.test(s)) {
    return { ok: false, reason: '統一編號需為 8 位數字' }
  }
  const multipliers = [1, 2, 1, 2, 1, 2, 4, 1]
  const sumDigits = (n: number) => Math.floor(n / 10) + (n % 10)
  let total = 0
  for (let i = 0; i < 8; i++) {
    total += sumDigits(Number(s[i]) * multipliers[i])
  }
  if (total % 10 === 0) return { ok: true }
  if (s[6] === '7' && (total + 1) % 10 === 0) return { ok: true }
  return { ok: false, reason: '統一編號檢核碼錯誤' }
}

const VendorList = () => {
  const { projectKey } = useParams<{ projectKey: string }>()
  const { showSuccess, showError } = useToast()
  const { currentProject } = useAppSelector(state => state.project)

  const { hasPermission } = usePermissions(currentProject?.id)
  const canManage = hasPermission(Permission.MANAGE_FINANCE_VENDORS)
  const canDelete = hasPermission(Permission.DELETE_FINANCE_VENDORS)
  const canView = hasPermission(Permission.VIEW_FINANCE_VENDORS)

  const [vendors, setVendors] = useState<Vendor[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [knownTags, setKnownTags] = useState<string[]>([])
  const [pagination, setPagination] = useState({ page: 1, totalPages: 1, total: 0 })

  const [search, setSearch] = useState('')
  const [typeFilter, setTypeFilter] = useState<'ALL' | VendorType>('ALL')
  const [tagFilter, setTagFilter] = useState('')
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useFinancePageSize()

  const [showModal, setShowModal] = useState(false)
  const [editingId, setEditingId] = useState<string | null>(null)
  const [formData, setFormData] = useState<FormState>(emptyForm)
  const [submitting, setSubmitting] = useState(false)
  const [tagInput, setTagInput] = useState('')

  const load = async () => {
    if (!currentProject?.id) return
    setLoading(true)
    setError(null)
    try {
      const res = await getVendors(currentProject.id, {
        page,
        limit: pageSize,
        search: search || undefined,
        type: typeFilter === 'ALL' ? undefined : typeFilter,
        tag: tagFilter || undefined,
      })
      if (res.success && res.data) {
        setVendors(res.data.vendors)
        setKnownTags(res.data.knownTags)
        setPagination({
          page: res.data.pagination.page,
          totalPages: res.data.pagination.totalPages,
          total: res.data.pagination.total,
        })
      }
    } catch (e: any) {
      const msg = e?.response?.data?.error || e?.message || '載入失敗'
      setError(msg)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    void load()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentProject?.id, page, pageSize, search, typeFilter, tagFilter])

  const openCreate = () => {
    setEditingId(null)
    setFormData(emptyForm)
    setTagInput('')
    setShowModal(true)
  }

  const openEdit = (v: Vendor) => {
    setEditingId(v.id)
    setFormData({
      type: v.type,
      name: v.name,
      taxId: v.taxId || '',
      contactName: v.contactName || '',
      contactPhone: v.contactPhone || '',
      address: v.address || '',
      tags: [...v.tags],
      note: v.note || '',
      bankAccounts: v.bankAccounts.map(b => ({
        _localId: b.id,
        id: b.id,
        accountName: b.accountName,
        bankName: b.bankName,
        accountNumber: b.accountNumber,
        isPrimary: b.isPrimary,
        sortOrder: b.sortOrder,
      })),
    })
    setTagInput('')
    setShowModal(true)
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!currentProject?.id) return
    if (!formData.name.trim()) {
      showError('資料不完整', '廠商名稱必填')
      return
    }
    for (let i = 0; i < formData.bankAccounts.length; i++) {
      const b = formData.bankAccounts[i]
      if (!b.accountName.trim() || !b.bankName.trim() || !b.accountNumber.trim()) {
        showError('銀行帳戶資料不完整', `第 ${i + 1} 筆戶名/銀行/帳號 必填`)
        return
      }
    }

    const payload: VendorCreateInput = {
      type: formData.type,
      name: formData.name.trim(),
      taxId: formData.taxId.trim() || null,
      contactName: formData.contactName.trim() || null,
      contactPhone: formData.contactPhone.trim() || null,
      address: formData.address.trim() || null,
      tags: formData.tags,
      note: formData.note.trim() || null,
      bankAccounts: formData.bankAccounts.map((b, idx) => ({
        accountName: b.accountName.trim(),
        bankName: b.bankName.trim(),
        accountNumber: b.accountNumber.trim(),
        isPrimary: !!b.isPrimary,
        sortOrder: idx,
      })),
    }

    setSubmitting(true)
    try {
      if (editingId) {
        await updateVendor(currentProject.id, editingId, payload)
        showSuccess('已更新廠商')
      } else {
        await createVendor(currentProject.id, payload)
        showSuccess('已建立廠商')
      }
      setShowModal(false)
      await load()
    } catch (e: any) {
      const msg = e?.response?.data?.error || e?.message || '操作失敗'
      showError(editingId ? '更新失敗' : '建立失敗', msg)
    } finally {
      setSubmitting(false)
    }
  }

  const handleDelete = async (v: Vendor) => {
    if (!currentProject?.id) return
    if (!confirm(`確定要刪除廠商「${v.name}」嗎？`)) return
    try {
      await deleteVendor(currentProject.id, v.id)
      showSuccess('廠商已刪除')
      await load()
    } catch (e: any) {
      const msg = e?.response?.data?.error || e?.message || '刪除失敗'
      showError('刪除失敗', msg)
    }
  }

  // tag chip 輸入
  const addTag = (t: string) => {
    const trimmed = t.trim()
    if (!trimmed) return
    if (formData.tags.includes(trimmed)) return
    setFormData(prev => ({ ...prev, tags: [...prev.tags, trimmed] }))
  }
  const removeTag = (t: string) => {
    setFormData(prev => ({ ...prev, tags: prev.tags.filter(x => x !== t) }))
  }

  const setBank = (idx: number, patch: Partial<FormBank>) => {
    setFormData(prev => ({
      ...prev,
      bankAccounts: prev.bankAccounts.map((b, i) => (i === idx ? { ...b, ...patch } : b)),
    }))
  }
  const removeBank = (idx: number) => {
    setFormData(prev => {
      const list = prev.bankAccounts.filter((_, i) => i !== idx)
      // 若刪掉的是主要帳戶，自動把第一筆設為主要
      if (list.length > 0 && !list.some(b => b.isPrimary)) {
        list[0] = { ...list[0], isPrimary: true }
      }
      return { ...prev, bankAccounts: list }
    })
  }
  const addBank = () => {
    setFormData(prev => {
      const b = newBank()
      const list = [...prev.bankAccounts, b]
      // 若是第一筆，設為主要
      if (list.length === 1) list[0].isPrimary = true
      return { ...prev, bankAccounts: list }
    })
  }
  const setPrimaryBank = (idx: number) => {
    setFormData(prev => ({
      ...prev,
      bankAccounts: prev.bankAccounts.map((b, i) => ({ ...b, isPrimary: i === idx })),
    }))
  }

  const tagSuggestions = useMemo(() => {
    if (!tagInput.trim()) return knownTags.filter(t => !formData.tags.includes(t)).slice(0, 8)
    const q = tagInput.toLowerCase()
    return knownTags
      .filter(t => !formData.tags.includes(t) && t.toLowerCase().includes(q))
      .slice(0, 8)
  }, [tagInput, knownTags, formData.tags])

  const renderPagination = (cls?: string) => (
    <div className={`px-4 py-2 flex items-center justify-between ${cls || ''}`}>
      <div className="flex items-center space-x-3">
        <div className="text-sm text-gray-700">
          共 {pagination.total} 筆
          {pagination.totalPages > 1 && (
            <>，第 {(page - 1) * pageSize + 1}-{Math.min(page * pageSize, pagination.total)} 筆</>
          )}
        </div>
        <div className="flex items-center space-x-1">
          <label className="text-sm text-gray-500">每頁</label>
          <select
            value={pageSize}
            onChange={(e) => { setPageSize(Number(e.target.value)); setPage(1) }}
            className="border border-gray-300 rounded px-2 py-1 text-sm focus:ring-blue-500 focus:border-blue-500"
          >
            {FINANCE_PAGE_SIZE_OPTIONS.map(s => <option key={s} value={s}>{s}</option>)}
          </select>
          <span className="text-sm text-gray-500">筆</span>
        </div>
      </div>
      {pagination.totalPages > 1 && (
        <div className="flex items-center space-x-2">
          <span className="text-sm text-gray-500">{pagination.page} / {pagination.totalPages} 頁</span>
          <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}
            className="p-2 rounded hover:bg-gray-100 disabled:opacity-50">
            <ChevronLeftIcon className="w-5 h-5" />
          </button>
          <button onClick={() => setPage(p => Math.min(pagination.totalPages, p + 1))} disabled={page === pagination.totalPages}
            className="p-2 rounded hover:bg-gray-100 disabled:opacity-50">
            <ChevronRightIcon className="w-5 h-5" />
          </button>
        </div>
      )}
    </div>
  )

  if (!canView) {
    return (
      <div className="p-6 max-w-2xl mx-auto mt-12 text-center bg-white rounded-lg shadow">
        <BuildingStorefrontIcon className="mx-auto h-12 w-12 text-gray-400" />
        <h2 className="mt-4 text-lg font-semibold text-gray-900">無權限存取</h2>
        <p className="mt-2 text-sm text-gray-500">您沒有檢視廠商管理的權限。</p>
        <Link to={`/project/${projectKey}/finance`} className="inline-flex items-center mt-4 text-blue-600 hover:underline">
          <ArrowLeftIcon className="w-4 h-4 mr-1" /> 返回財務首頁
        </Link>
      </div>
    )
  }

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <Link to={`/project/${projectKey}/finance`} className="text-gray-500 hover:text-gray-700">
            <ArrowLeftIcon className="w-5 h-5" />
          </Link>
          <BuildingStorefrontIcon className="w-7 h-7 text-blue-600" />
          <div>
            <h1 className="text-2xl font-bold text-gray-900">廠商管理</h1>
            <p className="text-sm text-gray-500">收支廠商與銀行帳戶資料</p>
          </div>
        </div>
        {canManage && (
          <button
            onClick={openCreate}
            className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
          >
            <PlusIcon className="w-4 h-4 mr-2" /> 新增廠商
          </button>
        )}
      </div>

      {/* Search + filters */}
      <div className="bg-white rounded-lg shadow p-4 space-y-3">
        <div className="flex items-center gap-3 flex-wrap">
          <div className="relative flex-1 min-w-[260px]">
            <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              placeholder="搜尋廠商名稱、統編、聯絡人、電話、地址、帳號…"
              value={search}
              onChange={e => { setSearch(e.target.value); setPage(1) }}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
          <select
            value={typeFilter}
            onChange={e => { setTypeFilter(e.target.value as any); setPage(1) }}
            className="border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="ALL">全部類型</option>
            <option value="INCOME">收入</option>
            <option value="EXPENSE">支出</option>
          </select>
          <select
            value={tagFilter}
            onChange={e => { setTagFilter(e.target.value); setPage(1) }}
            className="border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-blue-500 focus:border-blue-500 min-w-[150px]"
          >
            <option value="">全部標籤</option>
            {knownTags.map(t => <option key={t} value={t}>{t}</option>)}
          </select>
        </div>
      </div>

      {!loading && renderPagination('bg-white rounded-lg shadow')}

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-md p-4">
          <p className="text-sm text-red-700">{error}</p>
        </div>
      )}

      <div className="bg-white rounded-lg shadow overflow-hidden">
        {loading ? (
          <div className="p-6 text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
          </div>
        ) : vendors.length === 0 ? (
          <div className="p-6 text-center text-gray-500">
            {search || typeFilter !== 'ALL' || tagFilter ? '找不到符合條件的廠商' : '尚無廠商資料'}
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">類型</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">廠商名稱</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">聯絡人</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">電話</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase min-w-[180px]">標籤</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase min-w-[200px]">主要銀行帳戶</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">地址</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase sticky right-0 bg-gray-50 z-10 shadow-[-4px_0_4px_-2px_rgba(0,0,0,0.06)]">操作</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {vendors.map(v => {
                    const t = TYPE_LABEL[v.type]
                    const primary = v.bankAccounts.find(b => b.isPrimary) || v.bankAccounts[0]
                    return (
                      <tr key={v.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${t.cls}`}>{t.label}</span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-medium text-gray-900">{v.name}</div>
                          {v.taxId && (() => {
                            const check = validateTaiwanTaxId(v.taxId)
                            return (
                              <div className={`text-xs font-mono ${check.ok ? 'text-gray-500' : 'text-red-600'}`} title={check.ok ? undefined : check.reason}>
                                統編 {v.taxId}{!check.ok && ' ⚠'}
                              </div>
                            )
                          })()}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">{v.contactName || '-'}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">{v.contactPhone || '-'}</td>
                        <td className="px-6 py-4 text-sm">
                          {v.tags.length === 0 ? <span className="text-gray-400">-</span> : (
                            <div className="flex flex-wrap gap-1">
                              {v.tags.map(tag => (
                                <span key={tag} className="inline-flex items-center px-2 py-0.5 rounded text-xs bg-blue-100 text-blue-800">{tag}</span>
                              ))}
                            </div>
                          )}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-700">
                          {primary ? (
                            <div>
                              <div>{primary.bankName}</div>
                              <div className="text-xs text-gray-500">{primary.accountName}｜{primary.accountNumber}</div>
                              {v.bankAccounts.length > 1 && (
                                <div className="text-xs text-gray-400">+{v.bankAccounts.length - 1} 筆</div>
                              )}
                            </div>
                          ) : <span className="text-gray-400">-</span>}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-600 max-w-xs truncate" title={v.address || ''}>{v.address || '-'}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium sticky right-0 bg-white z-10 shadow-[-4px_0_4px_-2px_rgba(0,0,0,0.06)]">
                          {canManage && (
                            <button onClick={() => openEdit(v)} className="text-blue-600 hover:text-blue-900 mr-3" title="編輯">
                              <PencilIcon className="w-4 h-4" />
                            </button>
                          )}
                          {canDelete && (
                            <button onClick={() => handleDelete(v)} className="text-red-600 hover:text-red-900" title="刪除">
                              <TrashIcon className="w-4 h-4" />
                            </button>
                          )}
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
            {renderPagination()}
          </>
        )}
      </div>

      {/* 操作紀錄 */}
      <FinanceAuditLog section="vendors" title="廠商操作紀錄" limit={50} />

      {/* 新增/編輯 Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 overflow-y-auto">
          <div className="bg-white rounded-lg shadow-xl max-w-3xl w-full my-8">
            <form onSubmit={handleSubmit}>
              <div className="px-6 py-4 border-b flex items-center justify-between">
                <h3 className="text-lg font-semibold">{editingId ? '編輯廠商' : '新增廠商'}</h3>
                <button type="button" onClick={() => setShowModal(false)} className="text-gray-400 hover:text-gray-600">
                  <XMarkIcon className="w-5 h-5" />
                </button>
              </div>

              <div className="px-6 py-4 space-y-4 max-h-[70vh] overflow-y-auto">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">類型 <span className="text-red-500">*</span></label>
                    <div className="flex gap-2">
                      {(['EXPENSE', 'INCOME'] as VendorType[]).map(t => (
                        <button
                          type="button"
                          key={t}
                          onClick={() => setFormData(prev => ({ ...prev, type: t }))}
                          className={`px-4 py-1.5 rounded-md text-sm border ${formData.type === t ? `${TYPE_LABEL[t].cls} border-transparent` : 'border-gray-300 text-gray-600 hover:bg-gray-50'}`}
                        >
                          {TYPE_LABEL[t].label}
                        </button>
                      ))}
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">廠商名稱 <span className="text-red-500">*</span></label>
                    <input
                      type="text"
                      value={formData.name}
                      onChange={e => setFormData(prev => ({ ...prev, name: e.target.value }))}
                      className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-blue-500 focus:border-blue-500"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">統一編號</label>
                    <input
                      type="text"
                      value={formData.taxId}
                      onChange={e => setFormData(prev => ({ ...prev, taxId: e.target.value }))}
                      placeholder="例：12345678"
                      className={`w-full border rounded-md px-3 py-2 text-sm focus:ring-blue-500 focus:border-blue-500 font-mono ${
                        validateTaiwanTaxId(formData.taxId).ok ? 'border-gray-300' : 'border-red-400 focus:ring-red-500 focus:border-red-500'
                      }`}
                    />
                    {!validateTaiwanTaxId(formData.taxId).ok && (
                      <p className="mt-1 text-xs text-red-600">
                        ⚠ {validateTaiwanTaxId(formData.taxId).reason}（仍可儲存）
                      </p>
                    )}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">聯絡人</label>
                    <input
                      type="text"
                      value={formData.contactName}
                      onChange={e => setFormData(prev => ({ ...prev, contactName: e.target.value }))}
                      className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">聯絡電話</label>
                    <input
                      type="text"
                      value={formData.contactPhone}
                      onChange={e => setFormData(prev => ({ ...prev, contactPhone: e.target.value }))}
                      className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                  <div className="col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">地址</label>
                    <input
                      type="text"
                      value={formData.address}
                      onChange={e => setFormData(prev => ({ ...prev, address: e.target.value }))}
                      className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                </div>

                {/* 標籤 */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">標籤</label>
                  <div className="flex flex-wrap items-center gap-1.5 border border-gray-300 rounded-md p-2 min-h-[42px]">
                    {formData.tags.map(t => (
                      <span key={t} className="inline-flex items-center px-2 py-0.5 rounded text-xs bg-blue-100 text-blue-800">
                        {t}
                        <button type="button" onClick={() => removeTag(t)} className="ml-1 text-blue-700 hover:text-blue-900">
                          <XMarkIcon className="w-3 h-3" />
                        </button>
                      </span>
                    ))}
                    <input
                      type="text"
                      value={tagInput}
                      onChange={e => setTagInput(e.target.value)}
                      onKeyDown={e => {
                        if (e.key === 'Enter' || e.key === ',') {
                          e.preventDefault()
                          addTag(tagInput)
                          setTagInput('')
                        } else if (e.key === 'Backspace' && !tagInput && formData.tags.length > 0) {
                          removeTag(formData.tags[formData.tags.length - 1])
                        }
                      }}
                      placeholder="輸入後按 Enter 新增"
                      className="flex-1 min-w-[120px] outline-none text-sm bg-transparent"
                    />
                  </div>
                  {tagSuggestions.length > 0 && (
                    <div className="mt-1 flex flex-wrap gap-1">
                      <span className="text-xs text-gray-500">建議：</span>
                      {tagSuggestions.map(t => (
                        <button
                          key={t}
                          type="button"
                          onClick={() => { addTag(t); setTagInput('') }}
                          className="text-xs px-2 py-0.5 rounded border border-gray-200 text-gray-700 hover:bg-gray-50"
                        >
                          {t}
                        </button>
                      ))}
                    </div>
                  )}
                </div>

                {/* 銀行帳戶 */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label className="block text-sm font-medium text-gray-700">銀行帳戶</label>
                    <button type="button" onClick={addBank} className="text-sm text-blue-600 hover:text-blue-800 inline-flex items-center">
                      <PlusIcon className="w-4 h-4 mr-1" /> 新增帳戶
                    </button>
                  </div>
                  {formData.bankAccounts.length === 0 ? (
                    <div className="text-sm text-gray-400 border border-dashed border-gray-300 rounded-md p-4 text-center">
                      尚未新增銀行帳戶
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {formData.bankAccounts.map((b, idx) => (
                        <div key={b._localId} className="border border-gray-200 rounded-md p-3 space-y-2 bg-gray-50">
                          <div className="grid grid-cols-3 gap-2">
                            <div>
                              <label className="block text-xs text-gray-500 mb-1">戶名 <span className="text-red-500">*</span></label>
                              <input type="text" value={b.accountName}
                                onChange={e => setBank(idx, { accountName: e.target.value })}
                                className="w-full border border-gray-300 rounded-md px-2 py-1 text-sm focus:ring-blue-500 focus:border-blue-500" />
                            </div>
                            <div>
                              <label className="block text-xs text-gray-500 mb-1">銀行與分行 <span className="text-red-500">*</span></label>
                              <input type="text" value={b.bankName}
                                onChange={e => setBank(idx, { bankName: e.target.value })}
                                className="w-full border border-gray-300 rounded-md px-2 py-1 text-sm focus:ring-blue-500 focus:border-blue-500" />
                            </div>
                            <div>
                              <label className="block text-xs text-gray-500 mb-1">帳號 <span className="text-red-500">*</span></label>
                              <input type="text" value={b.accountNumber}
                                onChange={e => setBank(idx, { accountNumber: e.target.value })}
                                className="w-full border border-gray-300 rounded-md px-2 py-1 text-sm focus:ring-blue-500 focus:border-blue-500" />
                            </div>
                          </div>
                          <div className="flex items-center justify-between">
                            <label className="inline-flex items-center text-xs text-gray-700">
                              <input type="radio" name="primaryBank" checked={!!b.isPrimary}
                                onChange={() => setPrimaryBank(idx)}
                                className="mr-1" />
                              主要帳戶
                            </label>
                            <button type="button" onClick={() => removeBank(idx)}
                              className="text-xs text-red-600 hover:text-red-800 inline-flex items-center">
                              <TrashIcon className="w-3.5 h-3.5 mr-0.5" /> 移除
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* 備註 */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">備註</label>
                  <textarea
                    value={formData.note}
                    onChange={e => setFormData(prev => ({ ...prev, note: e.target.value }))}
                    rows={3}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>

                {/* 編輯時顯示該廠商操作紀錄 */}
                {editingId && (
                  <FinanceAuditLog
                    entityType="Vendor"
                    entityId={editingId}
                    title="此廠商操作紀錄"
                    compact
                    limit={20}
                  />
                )}
              </div>

              <div className="px-6 py-3 border-t bg-gray-50 flex items-center justify-end gap-2">
                <button type="button" onClick={() => setShowModal(false)}
                  className="px-4 py-2 border border-gray-300 rounded-md text-sm hover:bg-gray-100">取消</button>
                <button type="submit" disabled={submitting}
                  className="px-4 py-2 bg-blue-600 text-white rounded-md text-sm hover:bg-blue-700 disabled:opacity-50">
                  {submitting ? '儲存中…' : (editingId ? '更新' : '建立')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  )
}

export default VendorList
